import { dump } from "js-yaml";
import config from "config";
import User from "../entities/User.entity";
import { PermissionHierarchyType } from "models/Permission.model";
import { OrgRoleIds, RoleIds, WorkspaceRoleIds } from "models/Role.model";
import { DestType, HierarchyType } from "models/Secret.model";
import {
  IFileObject,
  IFileType,
  NotebookFileType,
  WorkspaceClassId,
  WorkspaceStatus,
} from "models/Workspace.model";
import React from "react";
import { useLocation } from "react-router-dom";
import { getIsCPUDefaultFromQuery } from "contexts/WorkspaceCreateContext";
import { CurrencyDisplay } from "./OrgPages/types";
import { getHealthCheckStatusKey } from "utils/healthChecks";
import { validateRemoteFile } from "server/devplane/remotefiles/api";
import { PROGRAMS } from "constants/index";

export function useQuery() {
  const { search } = useLocation();

  return React.useMemo(() => new URLSearchParams(search), [search]);
}

export const displayCPUs = (cid: WorkspaceClassId) => {
  switch (cid) {
    case WorkspaceClassId.TwoByTwo: {
      return 2.0;
    }
    case WorkspaceClassId.TwoByFour: {
      return 2.0;
    }
    case WorkspaceClassId.TwoByEight: {
      return 2.0;
    }
    case WorkspaceClassId.FourBySixteen: {
      return 4.0;
    }
    case WorkspaceClassId.EightByThirtyTwo: {
      return 8.0;
    }
    case WorkspaceClassId.SixteenByThirtyTwo: {
      return 16.0;
    }
  }
};

export const displayRAM = (cid: WorkspaceClassId) => {
  switch (cid) {
    case WorkspaceClassId.TwoByTwo: {
      return 2;
    }
    case WorkspaceClassId.TwoByFour: {
      return 4;
    }
    case WorkspaceClassId.TwoByEight: {
      return 8;
    }
    case WorkspaceClassId.FourBySixteen: {
      return 16;
    }
    case WorkspaceClassId.EightByThirtyTwo: {
      return 32;
    }
    case WorkspaceClassId.SixteenByThirtyTwo: {
      return 32;
    }
  }
};

export const displayWorkspaceStatus = (status: WorkspaceStatus) => {
  switch (status) {
    case WorkspaceStatus.Failure: {
      return "Error";
    }
    case WorkspaceStatus.Running: {
      return "Running";
    }
    case WorkspaceStatus.Stopping: {
      return "Stopping";
    }
    case WorkspaceStatus.Stopped: {
      return "Stopped";
    }
    case WorkspaceStatus.Starting: {
      return "Starting";
    }
    case WorkspaceStatus.Deploying: {
      return "Deploying";
    }
    case WorkspaceStatus.Deleting: {
      return "Deleting";
    }
  }
};

export const displayHierarchyType = (type: HierarchyType) => {
  switch (type) {
    case HierarchyType.Organization: {
      return "Organization";
    }
    case HierarchyType.User: {
      return "User";
    }
    case HierarchyType.Workspace: {
      return "Environment";
    }
  }
};

export const displayPermissionHierarchyType = (
  type: PermissionHierarchyType,
) => {
  switch (type) {
    case PermissionHierarchyType.Organization: {
      return "organization";
    }
    case PermissionHierarchyType.Workspace: {
      return "workspace";
    }
  }
};

export const displayDestType = (type: DestType) => {
  switch (type) {
    case DestType.EnvVariable: {
      return "Environment Variable";
    }
    case DestType.File: {
      return "File";
    }
  }
};

export const displayRoleId = (roleId: RoleIds) => {
  switch (roleId) {
    case OrgRoleIds.ViewOnly || WorkspaceRoleIds.Read: {
      return "View Only";
    }
    case OrgRoleIds.Admin || WorkspaceRoleIds.Write: {
      return "Read & Write";
    }
  }
};

export const displayGitRepo = (gitRepo: string) =>
  parseCloneFormatToGitUrl(gitRepo).slice(8);

export const destConfigKey = (type: DestType) => {
  switch (type) {
    case DestType.EnvVariable: {
      return "name";
    }
    case DestType.File: {
      return "path";
    }
  }
};

export const destConfigKeyInputValue = (type: DestType) => {
  switch (type) {
    case DestType.EnvVariable: {
      return "Name";
    }
    case DestType.File: {
      return "File Path";
    }
  }
};

export const getDefaultRoleId = (
  hierarchyType: PermissionHierarchyType,
): RoleIds => {
  switch (hierarchyType) {
    case PermissionHierarchyType.Organization: {
      return OrgRoleIds.Admin;
    }
    case PermissionHierarchyType.Workspace: {
      return WorkspaceRoleIds.Write;
    }
  }
};

export const getRoleIdOptions = (hierarchyType: PermissionHierarchyType) => {
  switch (hierarchyType) {
    case PermissionHierarchyType.Organization: {
      return OrgRoleIds;
    }
    case PermissionHierarchyType.Workspace: {
      return WorkspaceRoleIds;
    }
  }
};
// https://github.com/brevdev/brev-cli
// https://github.com/brevdev/brev.cli

export const getRepoProjectName = (gitRepo: string): string => {
  // const regex = /\/(?<projectName>[0-9a-zA-Z-]+)\.git$/;
  // const match = gitRepo.match(regex);
  // return match?.groups?.projectName === undefined
  //   ? ""
  //   : match.groups.projectName;
  const parts = gitRepo.split(".git")[0].split("/");
  return parts[parts.length - 1];
};

export const getCustomDirectoryFromAbsolutePathName = (
  path: string,
): string => {
  const regex =
    /(?!\/home\/brev\/workspace\/)(?<customDirectory>[0-9a-zA-Z-]+)$/;
  const match = path.match(regex);
  return match?.groups?.customDirectory === undefined
    ? ""
    : match.groups.customDirectory;
};

export const getRootDirectoryFromPath = (path: string): string => {
  const folders = path.split("/");
  return folders.length > 1 ? folders[0] : "";
};

export const capitalizeFirstLetter = (word: string): string =>
  word.charAt(0).toUpperCase() + word.slice(1);

export const fallbackCopyTextToClipboard = (
  text: string,
  insideModal = false,
) => {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(text).catch(() => {
      execCommandCopy(text, insideModal);
    });
    return;
  }
  execCommandCopy(text, insideModal);
};

const execCommandCopy = (text: string, insideModal = false) => {
  const textArea = document.createElement("textarea");
  textArea.value = text;

  // Avoid scrolling to bottom
  textArea.style.top = "0";
  textArea.style.left = "0";
  textArea.style.position = "fixed";

  if (insideModal) {
    document.getElementsByClassName("copypasta")[0].appendChild(textArea);
  } else {
    document.body.appendChild(textArea);
  }
  textArea.focus();
  textArea.select();

  try {
    document.execCommand("copy");
  } catch (err) {
    // showToast("unable to copy, please try manually", "error");
  }

  if (insideModal) {
    document.getElementsByClassName("copypasta")[0].removeChild(textArea);
  } else {
    document.body.removeChild(textArea);
  }
};

/*
    Takes a git url and converts it to a format like:
    gitlab.com:mygitlaborg/mycoolrepo.git
 */
export const parseGitUrlToCloneFormat = (url: string): string => {
  let cloneFormat = "";
  if (url.slice(0, 4) === "http") {
    const chunks = url.split("/");
    if (chunks.length > 4) {
      const urlRepoProvider = chunks[2];
      const urlRepoOrgName = chunks[3];
      const urlRepoName = chunks[4].split(".git")[0];
      cloneFormat = `${urlRepoProvider}:${urlRepoOrgName}/${urlRepoName}.git`;
    } else return "";
  } else if (url.slice(0, 4) === "git@") {
    cloneFormat = url.slice(4);
  }

  return cloneFormat;
};

/*
  Takes a git url and validates that it is a repository
  Valid formats accepted by the backend:
    github.com:brevdev/backend.git
    git@github.com:brevdev/backend.git
    https://github.com/brevdev/backend
    https://github.com/brevdev/backend.git
 */

// TODO(ishan): this function is broken
export const validateGitRepo = (
  url: string,
): { success: boolean; message?: string } => {
  if (url === "")
    return {
      success: false,
      message: "Must enter a git repository. Try again!",
    };
  const regex =
    /((git|http(s)?)|(git@[\w\.]+))(:(\/\/)?)([\w\.@\:/\-~]+)(\.git)?/;
  const success = regex.test(url);
  if (success) return { success };
  return { success, message: "Must be a valid git repository. Try again!" };
};

/*
  Given a git url of the type:
    1. git@github.com:brevdev/brev-cli.git
    2. https://github.com/brevdev/brev-cli
    3. https://github.com/brevdev/brev-cli.git

  This function will return either
    1. brevdev/brev-cli
    2. brevdev/brev-cli.git

  This is convenient for a quick summary view when not in focus.
  Ideally we'd remove the trailing .git but I don't feel like doing that now
 */
/*
    Takes a git url like:
        gitlab.com:mygitlaborg/mycoolrepo.git
    or this
        https://github.com/brevdev/hello-react.git

    and formats it to:
        https://gitlab.com/mygitlaborg/mycoolrepo
 */
export const parseCloneFormatToGitUrl = (cloneFormat: string) => {
  if (cloneFormat === "") return "";

  if (
    cloneFormat.slice(0, 8) === "https://" &&
    cloneFormat.slice(-4) === ".git"
  ) {
    return cloneFormat.slice(0, -4);
  }

  const chunks = cloneFormat.split(":");
  const urlRepoProvider = chunks[0];

  if (
    urlRepoProvider !== "github.com" &&
    urlRepoProvider !== "gitlab.com" &&
    urlRepoProvider !== "bitbucket.org"
  ) {
    const repo = chunks[1].split(".git")[0];
    return `https://${urlRepoProvider}/${repo}`;
  }
  const remainder = chunks[1].split("/");
  const org = remainder[0];
  const repo = remainder[1].split(".git")[0];

  return `https://${urlRepoProvider}/${org}/${repo}`;
};

export const getGithubOauthAuthorizationURL = (
  user: User | null,
  returnTo: string,
): URL => {
  const url = new URL(config.githubOauthAuthorizationEndpoint);
  url.searchParams.append("client_id", config.githubOauthClientID);
  // url.searchParams.append("redirect_uri", new URL(config.brevApiUrl + config.githubOauthRedirectRoute).href)
  const state: {
    user?: string;
    returnTo?: string;
  } = {};

  if (returnTo) {
    state.returnTo = returnTo;
  }

  if (user) {
    state.user = user.id;
  }
  const base64State = btoa(JSON.stringify(state));
  url.searchParams.append("state", base64State);
  url.searchParams.append("scope", config.githubOauthScope);
  return url;
};

export const validateName = (name: string): boolean => {
  if (name === "") return false;
  const regex = /^([0-9]|[a-z]|[A-Z])*([0-9a-zA-Z-]+)$/;
  return regex.test(name);
};

// Validates a tunnel name as a DNS label (RFC 1123).
// Returns an error message string, or empty string if valid.
export const validateTunnelName = (name: string): string => {
  if (!name) return "Name is required";
  if (name.length > 63) return "Name must be 63 characters or fewer";
  if (!/^[a-z0-9]/.test(name))
    return "Name must start with a lowercase letter or digit";
  if (!/^[a-z0-9-]+$/.test(name))
    return "Name may only contain lowercase letters, digits, and hyphens";
  if (!/[a-z0-9]$/.test(name))
    return "Name must end with a lowercase letter or digit";
  return "";
};

export function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

export interface LocationReadable {
  city: string;
  country: string;
  geo: string;
}

const REGION_BY_COUNTRY: Record<string, string> = {
  USA: "North America",
  Canada: "North America",
  Brazil: "South America",
  Finland: "Europe",
  Norway: "Europe",
  France: "Europe",
  Germany: "Europe",
  Netherlands: "Europe",
  Belgium: "Europe",
  UK: "Europe",
  Ireland: "Europe",
  Poland: "Europe",
  Spain: "Europe",
  Switzerland: "Europe",
  Italy: "Europe",
  Sweden: "Europe",
  Iceland: "Europe",
  India: "Asia Pacific",
  Japan: "Asia Pacific",
  Singapore: "Asia Pacific",
  Taiwan: "Asia Pacific",
  Australia: "Asia Pacific",
  "South Korea": "Asia Pacific",
  Indonesia: "Asia Pacific",
  Vietnam: "Asia Pacific",
  "South Africa": "Africa",
  Israel: "Middle East",
  Bahrain: "Middle East",
  UAE: "Middle East",
  Qatar: "Middle East",
  "Saudi Arabia": "Middle East",
  Chile: "South America",
};

export function deriveGeo(country: string): string {
  return REGION_BY_COUNTRY[country] || "Other";
}

export const regionToReadable: Record<string, LocationReadable> = {
  // AWS
  "us-east-1": { city: "Ashburn, VA", country: "USA", geo: "North America" },
  "us-east-2": { city: "Columbus, OH", country: "USA", geo: "North America" },
  "us-west-1": { city: "San Jose, CA", country: "USA", geo: "North America" },
  "us-west-2": { city: "Boardman, OR", country: "USA", geo: "North America" },
  "af-south-1": { city: "Cape Town", country: "South Africa", geo: "Africa" },
  "ap-east-1": { city: "Hong Kong", country: "Hong Kong", geo: "Asia Pacific" },
  "ap-south-1": { city: "Mumbai", country: "India", geo: "Asia Pacific" },
  "ap-northeast-1": { city: "Tokyo", country: "Japan", geo: "Asia Pacific" },
  "ap-northeast-2": {
    city: "Seoul",
    country: "South Korea",
    geo: "Asia Pacific",
  },
  "ap-northeast-3": { city: "Osaka", country: "Japan", geo: "Asia Pacific" },
  "ap-southeast-1": {
    city: "Singapore",
    country: "Singapore",
    geo: "Asia Pacific",
  },
  "ap-southeast-2": {
    city: "Sydney",
    country: "Australia",
    geo: "Asia Pacific",
  },
  "ap-southeast-3": {
    city: "Jakarta",
    country: "Indonesia",
    geo: "Asia Pacific",
  },
  "ca-central-1": { city: "Montreal", country: "Canada", geo: "North America" },
  "eu-central-1": { city: "Frankfurt", country: "Germany", geo: "Europe" },
  "eu-north-1": { city: "Stockholm", country: "Sweden", geo: "Europe" },
  "eu-south-1": { city: "Milan", country: "Italy", geo: "Europe" },
  "eu-west-1": { city: "Ireland", country: "Ireland", geo: "Europe" },
  "eu-west-2": { city: "London", country: "UK", geo: "Europe" },
  "eu-west-3": { city: "Paris", country: "France", geo: "Europe" },
  "il-central-1": { city: "Tel Aviv", country: "Israel", geo: "Middle East" },
  "me-central-1": { city: "Abu Dhabi", country: "UAE", geo: "Middle East" },
  "me-south-1": { city: "Bahrain", country: "Bahrain", geo: "Middle East" },
  "sa-east-1": { city: "São Paulo", country: "Brazil", geo: "South America" },
  // GCP
  "us-central1": {
    city: "Council Bluffs, IA",
    country: "USA",
    geo: "North America",
  },
  "us-east1": {
    city: "Moncks Corner, SC",
    country: "USA",
    geo: "North America",
  },
  "us-east4": { city: "Ashburn, VA", country: "USA", geo: "North America" },
  "us-east5": { city: "Columbus, OH", country: "USA", geo: "North America" },
  "us-west1": { city: "The Dalles, OR", country: "USA", geo: "North America" },
  "us-west2": { city: "Los Angeles, CA", country: "USA", geo: "North America" },
  "us-west3": {
    city: "Salt Lake City, UT",
    country: "USA",
    geo: "North America",
  },
  "us-west4": { city: "Las Vegas, NV", country: "USA", geo: "North America" },
  "asia-east1": {
    city: "Changhua County",
    country: "Taiwan",
    geo: "Asia Pacific",
  },
  "asia-east2": {
    city: "Hong Kong",
    country: "Hong Kong",
    geo: "Asia Pacific",
  },
  "asia-south1": { city: "Mumbai", country: "India", geo: "Asia Pacific" },
  "asia-southeast1": {
    city: "Jurong West",
    country: "Singapore",
    geo: "Asia Pacific",
  },
  "asia-southeast2": {
    city: "Jakarta",
    country: "Indonesia",
    geo: "Asia Pacific",
  },
  "asia-northeast1": { city: "Tokyo", country: "Japan", geo: "Asia Pacific" },
  "asia-northeast3": {
    city: "Seoul",
    country: "South Korea",
    geo: "Asia Pacific",
  },
  "australia-southeast1": {
    city: "Sydney",
    country: "Australia",
    geo: "Asia Pacific",
  },
  "europe-west1": { city: "St. Ghislain", country: "Belgium", geo: "Europe" },
  "europe-west2": { city: "London", country: "UK", geo: "Europe" },
  "europe-west3": { city: "Frankfurt", country: "Germany", geo: "Europe" },
  "europe-west4": { city: "Eemshaven", country: "Netherlands", geo: "Europe" },
  "europe-west6": { city: "Zurich", country: "Switzerland", geo: "Europe" },
  "europe-southwest1": { city: "Madrid", country: "Spain", geo: "Europe" },
  "europe-central2": { city: "Warsaw", country: "Poland", geo: "Europe" },
  "me-central2": {
    city: "Dammam",
    country: "Saudi Arabia",
    geo: "Middle East",
  },
  "me-west1": { city: "Tel Aviv", country: "Israel", geo: "Middle East" },
  "africa-south1": {
    city: "Johannesburg",
    country: "South Africa",
    geo: "Africa",
  },
  "asia-northeast2": { city: "Osaka", country: "Japan", geo: "Asia Pacific" },
  "asia-south2": { city: "Delhi", country: "India", geo: "Asia Pacific" },
  "australia-southeast2": {
    city: "Melbourne",
    country: "Australia",
    geo: "Asia Pacific",
  },
  "europe-north1": { city: "Hamina", country: "Finland", geo: "Europe" },
  "europe-west8": { city: "Milan", country: "Italy", geo: "Europe" },
  "europe-west9": { city: "Paris", country: "France", geo: "Europe" },
  "europe-west10": { city: "Berlin", country: "Germany", geo: "Europe" },
  "europe-west12": { city: "Turin", country: "Italy", geo: "Europe" },
  "me-central1": { city: "Doha", country: "Qatar", geo: "Middle East" },
  "southamerica-west1": {
    city: "Santiago",
    country: "Chile",
    geo: "South America",
  },
  "us-south1": { city: "Dallas, TX", country: "USA", geo: "North America" },
  "northamerica-northeast1": {
    city: "Montreal",
    country: "Canada",
    geo: "North America",
  },
  "northamerica-northeast2": {
    city: "Toronto",
    country: "Canada",
    geo: "North America",
  },
  "southamerica-east1": {
    city: "São Paulo",
    country: "Brazil",
    geo: "South America",
  },
  // Crusoe
  "us-east1-a": { city: "Abilene, TX", country: "USA", geo: "North America" },
  "us-southcentral1-a": {
    city: "Claude, TX",
    country: "USA",
    geo: "North America",
  },
  "us-west1-a": {
    city: "The Dalles, OR",
    country: "USA",
    geo: "North America",
  },
  "eu-iceland1-a": { city: "Blönduós", country: "Iceland", geo: "Europe" },
  // Nebius
  "eu-north1": { city: "Mäntsälä", country: "Finland", geo: "Europe" },
  "eu-west1": { city: "Ireland", country: "Ireland", geo: "Europe" },
  // Lambda
  "us-east-3": { city: "US East", country: "USA", geo: "North America" },
  "us-south-1": { city: "Dallas, TX", country: "USA", geo: "North America" },
  "us-south-2": { city: "Dallas, TX", country: "USA", geo: "North America" },
  "us-south-3": { city: "Dallas, TX", country: "USA", geo: "North America" },
  "us-west-3": {
    city: "Salt Lake City, UT",
    country: "USA",
    geo: "North America",
  },
  "us-midwest-1": { city: "Chicago, IL", country: "USA", geo: "North America" },
  "asia-northeast-1": { city: "Tokyo", country: "Japan", geo: "Asia Pacific" },
  "asia-northeast-2": { city: "Osaka", country: "Japan", geo: "Asia Pacific" },
  "asia-south-1": { city: "Mumbai", country: "India", geo: "Asia Pacific" },
  "australia-east-1": {
    city: "Sydney",
    country: "Australia",
    geo: "Asia Pacific",
  },
  "australia-southeast-1": {
    city: "Sydney",
    country: "Australia",
    geo: "Asia Pacific",
  },
  "europe-central-1": { city: "Frankfurt", country: "Germany", geo: "Europe" },
  "europe-south-1": { city: "Milan", country: "Italy", geo: "Europe" },
  "me-west-1": { city: "Tel Aviv", country: "Israel", geo: "Middle East" },
  // Shadeform — Canada
  "montreal-canada-2": {
    city: "Montreal",
    country: "Canada",
    geo: "North America",
  },
  "montreal-canada-4": {
    city: "Montreal",
    country: "Canada",
    geo: "North America",
  },
  "calgary-canada-1": {
    city: "Calgary",
    country: "Canada",
    geo: "North America",
  },
  "toronto-canada-1": {
    city: "Toronto",
    country: "Canada",
    geo: "North America",
  },
  "CANADA-2": { city: "Canada", country: "Canada", geo: "North America" },
  // Shadeform — US Midwest
  "desmoines-usa-1": {
    city: "Des Moines, IA",
    country: "USA",
    geo: "North America",
  },
  "desmoines-usa-2": {
    city: "Des Moines, IA",
    country: "USA",
    geo: "North America",
  },
  "desmoines-usa-3": {
    city: "Des Moines, IA",
    country: "USA",
    geo: "North America",
  },
  "desmoines-usa-4": {
    city: "Des Moines, IA",
    country: "USA",
    geo: "North America",
  },
  "kansascity-usa-1": {
    city: "Kansas City, MO",
    country: "USA",
    geo: "North America",
  },
  "kansascity-usa-2": {
    city: "Kansas City, MO",
    country: "USA",
    geo: "North America",
  },
  "kansascity-usa-4": {
    city: "Kansas City, MO",
    country: "USA",
    geo: "North America",
  },
  "kansascity-usa-5": {
    city: "Kansas City, MO",
    country: "USA",
    geo: "North America",
  },
  "kansascity-usa-6": {
    city: "Kansas City, MO",
    country: "USA",
    geo: "North America",
  },
  "omaha-usa-1": { city: "Omaha, NE", country: "USA", geo: "North America" },
  "wichita-usa-1": {
    city: "Wichita, KS",
    country: "USA",
    geo: "North America",
  },
  "chicago-usa-1": {
    city: "Chicago, IL",
    country: "USA",
    geo: "North America",
  },
  "chicago-usa-3": {
    city: "Chicago, IL",
    country: "USA",
    geo: "North America",
  },
  "chicago-usa-4": {
    city: "Chicago, IL",
    country: "USA",
    geo: "North America",
  },
  "dowagiac-usa-1": {
    city: "Dowagiac, MI",
    country: "USA",
    geo: "North America",
  },
  // Shadeform — US East
  "ashburn-usa-7": {
    city: "Ashburn, VA",
    country: "USA",
    geo: "North America",
  },
  "beltsville-usa-1": {
    city: "Beltsville, MD",
    country: "USA",
    geo: "North America",
  },
  "dulles-usa-4": { city: "Dulles, VA", country: "USA", geo: "North America" },
  "newyork-usa-1": {
    city: "New York, NY",
    country: "USA",
    geo: "North America",
  },
  "newyork-usa-2": {
    city: "New York, NY",
    country: "USA",
    geo: "North America",
  },
  "newark-usa-1": { city: "Newark, NJ", country: "USA", geo: "North America" },
  "charlotte-usa-1": {
    city: "Charlotte, NC",
    country: "USA",
    geo: "North America",
  },
  "charlotte-usa-2": {
    city: "Charlotte, NC",
    country: "USA",
    geo: "North America",
  },
  "atlanta-usa-1": {
    city: "Atlanta, GA",
    country: "USA",
    geo: "North America",
  },
  "raleigh-usa-1": {
    city: "Raleigh, NC",
    country: "USA",
    geo: "North America",
  },
  "raleigh-usa-2": {
    city: "Raleigh, NC",
    country: "USA",
    geo: "North America",
  },
  // Shadeform — US South/West
  "houston-usa-1": {
    city: "Houston, TX",
    country: "USA",
    geo: "North America",
  },
  "houston-usa-3": {
    city: "Houston, TX",
    country: "USA",
    geo: "North America",
  },
  "dallas-usa-1": { city: "Dallas, TX", country: "USA", geo: "North America" },
  "dallas-usa-4": { city: "Dallas, TX", country: "USA", geo: "North America" },
  "dallas-usa-5": { city: "Dallas, TX", country: "USA", geo: "North America" },
  "dallas-usa-6": { city: "Dallas, TX", country: "USA", geo: "North America" },
  "casper-usa-1": { city: "Casper, WY", country: "USA", geo: "North America" },
  "sanjose-usa-1": {
    city: "San Jose, CA",
    country: "USA",
    geo: "North America",
  },
  "santaclara-usa-1": {
    city: "Santa Clara, CA",
    country: "USA",
    geo: "North America",
  },
  "losangeles-usa-1": {
    city: "Los Angeles, CA",
    country: "USA",
    geo: "North America",
  },
  "seattle-usa-1": {
    city: "Seattle, WA",
    country: "USA",
    geo: "North America",
  },
  // Shadeform — Europe
  AMS: { city: "Amsterdam", country: "Netherlands", geo: "Europe" },
  "amsterdam-netherlands-1": {
    city: "Amsterdam",
    country: "Netherlands",
    geo: "Europe",
  },
  "amsterdam-netherlands-2": {
    city: "Amsterdam",
    country: "Netherlands",
    geo: "Europe",
  },
  "helsinki-finland-1": { city: "Helsinki", country: "Finland", geo: "Europe" },
  "helsinki-finland-2": { city: "Helsinki", country: "Finland", geo: "Europe" },
  "helsinki-finland-4": { city: "Helsinki", country: "Finland", geo: "Europe" },
  "helsinki-finland-5": { city: "Helsinki", country: "Finland", geo: "Europe" },
  "oslo-norway-1": { city: "Oslo", country: "Norway", geo: "Europe" },
  "oslo-norway-3": { city: "Oslo", country: "Norway", geo: "Europe" },
  "kristiansand-norway-2": {
    city: "Kristiansand",
    country: "Norway",
    geo: "Europe",
  },
  "paris-france-1": { city: "Paris", country: "France", geo: "Europe" },
  "paris-france-3": { city: "Paris", country: "France", geo: "Europe" },
  "paris-france-5": { city: "Paris", country: "France", geo: "Europe" },
  "paris-france-6": { city: "Paris", country: "France", geo: "Europe" },
  "warsaw-poland-1": { city: "Warsaw", country: "Poland", geo: "Europe" },
  "london-uk-1": { city: "London", country: "UK", geo: "Europe" },
  "frankfurt-germany-5": {
    city: "Frankfurt",
    country: "Germany",
    geo: "Europe",
  },
  "frankfurt-germany-6": {
    city: "Frankfurt",
    country: "Germany",
    geo: "Europe",
  },
  "blonduos-iceland-2": { city: "Blönduós", country: "Iceland", geo: "Europe" },
  "blonduos-iceland-3": { city: "Blönduós", country: "Iceland", geo: "Europe" },
  "skarholmen-sweden-1": {
    city: "Skärholmen",
    country: "Sweden",
    geo: "Europe",
  },
  "smedjebacken-sweden-1": {
    city: "Smedjebacken",
    country: "Sweden",
    geo: "Europe",
  },
  "stockholm-sweden-2": { city: "Stockholm", country: "Sweden", geo: "Europe" },
  // Shadeform — Asia Pacific
  "tokyo-japan-2": { city: "Tokyo", country: "Japan", geo: "Asia Pacific" },
  "tokyo-japan-3": { city: "Tokyo", country: "Japan", geo: "Asia Pacific" },
  TYO4: { city: "Tokyo", country: "Japan", geo: "Asia Pacific" },
  "singapore-singapore-1": {
    city: "Singapore",
    country: "Singapore",
    geo: "Asia Pacific",
  },
  "bangalore-india-1": {
    city: "Bangalore",
    country: "India",
    geo: "Asia Pacific",
  },
  "hanoi-vietnam-1": { city: "Hanoi", country: "Vietnam", geo: "Asia Pacific" },
  "melbourne-australia-1": {
    city: "Melbourne",
    country: "Australia",
    geo: "Asia Pacific",
  },
  "sydney-australia-1": {
    city: "Sydney",
    country: "Australia",
    geo: "Asia Pacific",
  },
  "sydney-australia-3": {
    city: "Sydney",
    country: "Australia",
    geo: "Asia Pacific",
  },
  SYD2: { city: "Sydney", country: "Australia", geo: "Asia Pacific" },
  // Launchpad / OCI / sfcompute
  pdx: { city: "Portland, OR", country: "USA", geo: "North America" },
  atl: { city: "Atlanta, GA", country: "USA", geo: "North America" },
  southcentralus: {
    city: "San Antonio, TX",
    country: "USA",
    geo: "North America",
  },
  "uk-london-1": { city: "London", country: "UK", geo: "Europe" },
  "us-ashburn-1": { city: "Ashburn, VA", country: "USA", geo: "North America" },
  "us-chicago-1": { city: "Chicago, IL", country: "USA", geo: "North America" },
  richmond: { city: "Richmond, VA", country: "USA", geo: "North America" },
  "europe-west4-b": {
    city: "Eemshaven",
    country: "Netherlands",
    geo: "Europe",
  },
  "us-central1-c": {
    city: "Council Bluffs, IA",
    country: "USA",
    geo: "North America",
  },
};

// Tech Debt Consolidation here...

export const CPUTypes = {
  "t3a.medium": { cpu: 2, ram: 4 },
  "t3a.large": { cpu: 2, ram: 8 },
  "t3a.xlarge": { cpu: 4, ram: 16 },
  "t3a.2xlarge": { cpu: 8, ram: 32 },
  "t3.medium": { cpu: 2, ram: 4 },
  "t3.large": { cpu: 2, ram: 8 },
  "t3.xlarge": { cpu: 4, ram: 16 },
  "t3.2xlarge": { cpu: 8, ram: 32 },
};

export const NamesCPUTypes = {
  "2x4": "t3.medium",
  "2x8": "t3.large",
  "4x16": "t3.xlarge",
  "8x32": "t3.2xlarge",
};

export const isCPU_techdebt = (instance: string) => {
  try {
    return getIsCPUDefaultFromQuery(instance);
  } catch (error) {
    return false;
  }
};

export function memoryToGiB(memory: string): number {
  const units = {
    B: 1 / (1024 * 1024 * 1024),
    KB: 1000 / 1024 ** 3,
    MB: 1000 ** 2 / 1024 ** 3,
    GB: 1000 ** 3 / 1024 ** 3,
    TB: 1000 ** 4 / 1024 ** 3,
    PB: 1000 ** 5 / 1024 ** 3,
    KiB: 1 / 1024 ** 2,
    MiB: 1 / 1024,
    GiB: 1,
    TiB: 1024,
    PiB: 1024 ** 2,
  };

  let totalGiB = 0;

  // Regular expression to match numbers followed by unit names
  const regex = /(\d+(?:\.\d+)?)([A-Z][iB]+)/g;
  let match;

  while ((match = regex.exec(memory)) !== null) {
    const value = parseFloat(match[1]);
    const unit = match[2];
    if (units[unit] !== undefined) {
      totalGiB += value * units[unit];
    }
  }

  return totalGiB;
}

export const parseStatusMessage = (message: string) => {
  return message.replaceAll("rpc error: code = Internal desc =", " ");
};

export const integerValidationOrEmpty = (value: string) => {
  // Allow empty strings
  if (value === "") {
    return true;
  }

  // Check if the value is an integer
  return /^-?\d+$/.test(value);
};

// is integer greater than 20
export const integerValidationGreaterThan20OrEmpty = (value: string) => {
  // Allow empty strings
  if (value === "") {
    return true;
  }
  const regex = /^\d+$/; // This regular expression matches only integer values
  return regex.test(value) && parseInt(value) > 20;
};

export function mapValuesToArray<T>(objectMap: { [key: string]: T }): T[] {
  return Object.values(objectMap);
}

export function arrayToPathMap(fileObjects: IFileObject[]): {
  [path: string]: IFileObject;
} {
  const map: { [path: string]: IFileObject } = {};

  fileObjects.forEach((fileObject) => {
    map[fileObject.path] = fileObject;
  });

  return map;
}

export function replaceBlobWithRaw(url: string): string {
  const githubDomain = "github.com";
  const gitlabDomain = "gitlab.com";

  if (url.includes(githubDomain) && url.includes("/blob/")) {
    return url.replace("/blob/", "/raw/");
  } else if (url.includes(gitlabDomain) && url.includes("/-/blob/")) {
    return url.replace("/-/blob/", "/-/raw/");
  }

  return url;
}

export function replaceRawWithBlob(url: string): string {
  const githubDomain = "github.com";
  const gitlabDomain = "gitlab.com";

  if (url.includes(githubDomain) && url.includes("/raw/")) {
    return url.replace("/raw/", "/blob/");
  } else if (url.includes(gitlabDomain) && url.includes("/raw/")) {
    return url.replace("/raw/", "/-/blob/");
  }

  return url;
}

export interface NotebookItem {
  name: string;
  url: string;
  imageUrl: string;
  queryParams: string;
}

export const notebooks: NotebookItem[] = [
  {
    name: "Fine-tune Mistral 7B on your own data",
    url: "https://github.com/brevdev/notebooks/blob/main/mistral-finetune-own-data.ipynb",
    imageUrl: "https://brev.dev/_next/static/media/mistral.e780928b.jpg",
    queryParams:
      "instance=A10G:g5.xlarge&diskStorage=256&name=mistral-finetune-own-data&python=3.10&cuda=12.1.1",
  },
  // {
  //   name: "Run ControlNet with A utomatic1111 Stable Diffusion Web UI",
  //   url: "https://github.com/brevdev/notebooks/raw/main/mistral-finetune-own-data.ipynb",
  //   imageUrl: "https://brev.dev/_next/static/media/obey.57242832.png",
  //   queryParams: "",
  // },
  {
    name: "Deploy an AI/ML model to Replicate",
    url: "https://github.com/brevdev/notebooks/raw/main/deploy-to-replicate.ipynb",
    imageUrl:
      "https://uohmivykqgnnbiouffke.supabase.co/storage/v1/object/public/landingpage/brevreplicate1.png",
    queryParams: "",
  },
  {
    name: "Fine-tune Llama 2",
    url: "https://github.com/brevdev/notebooks/raw/main/llama2-finetune.ipynb",
    imageUrl:
      "https://brev.dev/_next/static/media/llama2-finetune.9d72b21a.png",
    queryParams:
      "instance=A10G:g5.xlarge&diskStorage=256&name=llama2-finetune&python=3.10&cuda=12.1.1",
  },
  {
    name: "Run Julia Lang",
    url: "https://github.com/brevdev/notebooks/raw/main/julia-install.ipynb",
    imageUrl:
      "https://uohmivykqgnnbiouffke.supabase.co/storage/v1/object/public/landingpage/julia.png",
    queryParams: "",
  },
];

export const getPathFromUrl = (url: string, host?: boolean) => {
  // TODO(ishan): this is currently hardcoded to the rapids notebook repo we set in src/components/Verb/utils/index.tsx
  if (url.includes("rapids")) {
    return `/home/rapids/notebooks/${url.split("/").pop()}`;
  }
  if (host) {
    return `./verb-workspace/${url.split("/").pop()}`;
  } else {
    return `./verb-workspace/${url.split("/").pop()}`;
  }
};

const checkPublicity = async (url: string): Promise<string | null> => {
  try {
    const result = await validateRemoteFile(url);
    return result.public
      ? null
      : result.errorMessage || "Repository is private";
  } catch {
    return "Failed to validate publicity. Please ensure this is a public repository";
  }
};

const gitlabNotebookRegex =
  /^https:\/\/(?:www\.)?gitlab\.com\/[\w-]+\/[\w-]+\/-\/(?:blob|raw)\/[\w-]+\/[\w\/-]+\.ipynb$/;
const githubNotebookRegex =
  /^https?:\/\/github\.com\/[^\/]+\/[^\/]+\/(?:blob|raw)\/[^\/]+(\/[^\/]+)*\/.*\.ipynb$/;
const colabRegex =
  /^https:\/\/colab\.research\.google\.com\/drive\/([a-zA-Z0-9_-]+)(\/)?(?=[?#]|$)/;
const githubRepoRegex = /^https?:\/\/github\.com\/[^\/]+\/[^\/]+$/;
const gitlabRepoRegex =
  /^https?:\/\/(?:www\.)?gitlab\.com\/[\w.-]+\/[\w.-]+(?:\/)?$/;

export const getNotebookFileTypeFromUrl = (url: string) => {
  if (githubNotebookRegex.test(url)) {
    return NotebookFileType.GithubFileType;
  } else if (gitlabNotebookRegex.test(url)) {
    return NotebookFileType.GitlabFileType;
  } else if (colabRegex.test(url)) {
    return NotebookFileType.ColabFileType;
  }
  return NotebookFileType.GithubFileType;
};

const isValidNotebookUrl = async (url: string): Promise<string | null> => {
  const validGitHubCheck = githubNotebookRegex.test(url);
  const validGitLabCheck = gitlabNotebookRegex.test(url);
  if (!validGitHubCheck && !validGitLabCheck) {
    return "Invalid Jupyter notebook URL. Make sure this is a GitHub or GitLab URL that ends in .ipynb";
  }
  const publicityCheck = await checkPublicity(url);
  return publicityCheck ? publicityCheck : null;
};

const isValidColabUrl = async (url: string): Promise<string | null> => {
  const validRegexCheck = colabRegex.test(url);
  if (!validRegexCheck) {
    return "Invalid Google Colab URL. Make sure this is a Google Colab URL";
  }
  const publicityCheck = await checkPublicity(url);
  return publicityCheck ? publicityCheck : null;
};

export const githubRepoRegexCheck = (url: string): boolean => {
  return githubRepoRegex.test(url);
};

export const gitlabRepoRegexCheck = (url: string): boolean => {
  return gitlabRepoRegex.test(url);
};

const isValidGithubRepoUrl = async (url: string): Promise<string | null> => {
  const validRegexCheck = githubRepoRegexCheck(url);
  if (!validRegexCheck) {
    return "Invalid GitHub repository URL. Ensure the form is https://github.com/<username>/<repo>";
  }
  const publicityCheck = await checkPublicity(url);
  return publicityCheck ? publicityCheck : null;
};

const isValidGitlabRepoUrl = async (url: string): Promise<string | null> => {
  const validRegexCheck = gitlabRepoRegexCheck(url);
  if (!validRegexCheck) {
    return "Invalid Gitlab repository URL. Ensure the form is https://gitlab.com/<username>/<repo>";
  }
  const publicityCheck = await checkPublicity(url);
  return publicityCheck ? publicityCheck : null;
};

export const sanitizeRepo = (url: string): string => {
  return url.endsWith(".git") ? url.slice(0, -4) : url;
};

export const isValidFileUrl = async (url: string): Promise<string | null> => {
  let errorMessage: string | null = null;

  if (url.includes(".ipynb")) {
    errorMessage = await isValidNotebookUrl(url);
    if (errorMessage) {
      return errorMessage;
    }
    return null;
  } else if (url.includes("colab")) {
    errorMessage = await isValidColabUrl(url);
    if (errorMessage) {
      return errorMessage;
    }
    return null;
  } else if (githubRepoRegexCheck(url)) {
    url = sanitizeRepo(url);
    errorMessage = await isValidGithubRepoUrl(url);
    if (errorMessage) {
      return errorMessage;
    }
    return null;
  } else if (gitlabRepoRegexCheck(url)) {
    url = sanitizeRepo(url);
    errorMessage = await isValidGitlabRepoUrl(url);
    if (errorMessage) {
      return errorMessage;
    }
    console.log("We are herere");
    return null;
  }
  return "Invalid File Format";
};

export const validateAndGetFileType = async (url: string) => {
  let errorMessage = await isValidGithubRepoUrl(url);
  if (!errorMessage) {
    return IFileType.GithubRepoFileType;
  }

  errorMessage = await isValidNotebookUrl(url);
  if (!errorMessage) {
    return IFileType.NotebookFileType;
  }

  errorMessage = await isValidColabUrl(url);
  if (!errorMessage) {
    return IFileType.ColabFileType;
  }

  return IFileType.NotebookFileType;
};

export function yamlRawDump(config: any): string {
  return dump(config, { lineWidth: -1 });
}

export const fetchOrgProfileImage = async (
  orgUrl: string,
): Promise<string | null> => {
  if (!orgUrl.includes("github.com/")) return null;

  const parts = orgUrl.split("/");
  const githubIndex = parts.findIndex((part) => part === "github.com");
  const orgName = parts[githubIndex + 1];
  if (!orgName) return null;

  try {
    const response = await fetch(`https://api.github.com/orgs/${orgName}`);
    const data = await response.json();
    return data.avatar_url;
  } catch (error) {
    console.error("Error fetching organization data:", error);
    return null;
  }
};

export async function fetchNotebookList() {
  const response = await fetch("/Local-Notebooks/files.json");
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  return data;
}

export async function fetchLegacyNotebookList() {
  const response = await fetch("/Local-Notebooks/legacy_files.json");
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  const data = await response.json();
  return data;
}

export const getImageUrl = async (
  notebookUrl: string,
  notebooks: any[],
  defaultImageUrl: string,
): Promise<string> => {
  let nbUrlRaw = replaceRawWithBlob(notebookUrl);
  const notebook = notebooks.find((n) => n.url === nbUrlRaw);
  if (notebook && notebook.imageUrl) {
    return notebook.imageUrl;
  }

  const orgImageUrl = await fetchOrgProfileImage(notebookUrl);
  return orgImageUrl || defaultImageUrl;
};
export function sanitizeNotebookUrl(url: string): string {
  if (url.includes("trtmistral1.html")) {
    return "Mistral 7B with NVIDIA TensorRT-LLM";
  }

  if (url.includes("mistral_nemo_finetune.html")) {
    return "Finetune and deploy Mistral with NVIDIA NeMo";
  }

  if (url.includes("question_answer_nemo.html")) {
    return "Train Question/Answer models using NeMo";
  }

  if (url.includes("streamingllm-tensorrt-llm.html")) {
    return "Using the StreamingLLM framework with TensorRT-LLM";
  }

  if (url.includes("diffusion_lora_inf.html")) {
    return "Stable Diffusion XL inference using LoRA adaptors";
  }
  if (url.includes("dbrx_inference.html")) {
    return "Optimizing inference on DBRX using VLLM and Gradio";
  }
  if (url.includes("llama3_finetune_inference.html")) {
    return "Finetuning Llama3 using SFT";
  }
  if (url.includes("llava-finetune.html")) {
    return "Finetune and Deploy the multimodal LLaVA model";
  }
  if (url.includes("llama3-tensorrtllm-deployment.html")) {
    return "Deploy Llama3 with TensorRT-LLM";
  }
  if (url.includes("rapids_cudf_pandas.html")) {
    return "Using cuDF Pandas to accelerate Data Science Workflows";
  }
  if (url.includes("efficientvit-segmentation.html")) {
    return "Efficient Vision Transformers for Image Classification and Segmentation";
  }
  if (url.includes("llama3dpo.html")) {
    return "Finetune Llama3 using Direct Preference Optimization";
  }

  return url
    .split(".")
    .slice(0, -1)
    .join(".")
    .split("-")
    .map((word) => {
      // Ensure specific abbreviations are capitalized and Phi2 is spelled Phi-2
      const specialWords = ["UI", "GGUF", "OCR", "PDF", "PHI2"];
      if (specialWords.includes(word.toUpperCase())) {
        if (word.toUpperCase() === "PHI2") {
          return "Phi-2";
        }
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}
export function isSharedWorkspace(
  workspace: any,
  currentUserID: string,
): boolean {
  return (
    workspace &&
    workspace.additionalUsers &&
    workspace.additionalUsers?.length > 0 &&
    !!workspace.additionalUsers.find((userId) => userId === currentUserID)
  );
}

const getHealthCheckStatusCounts = (healthChecks: any[]) => {
  const counts = new Map<string, number>();
  healthChecks.forEach((healthCheck) => {
    const key = getHealthCheckStatusKey(healthCheck);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  });
  return counts;
};

export const isCheckHealthSame = (a: any[], b: any[]) => {
  if (a === b) return true;
  if (a == null || b == null) return false;
  if (a.length !== b.length) return false;

  // Current callers only care whether each health check's status changed; API
  // array order is not meaningful here. Prefer probe port because health check
  // IDs may not be populated on early runtime responses.
  const statusCounts = getHealthCheckStatusCounts(a);

  for (const healthCheck of b) {
    const key = getHealthCheckStatusKey(healthCheck);
    const count = statusCounts.get(key);
    if (!count) return false;
    if (count === 1) {
      statusCounts.delete(key);
    } else {
      statusCounts.set(key, count - 1);
    }
  }

  return statusCounts.size === 0;
};

export function areStringListsEqual(
  list1: string[] | null | undefined,
  list2: string[] | null | undefined,
): boolean {
  if (list1 == null && list2 == null) {
    return true;
  }
  if (list1 == null || list2 == null) {
    return false;
  }
  if (list1.length !== list2.length) {
    return false;
  }
  for (let i = 0; i < list1.length; i++) {
    if (list1[i] !== list2[i]) {
      return false;
    }
  }
  return true;
}

export function timeout(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const displayInstanceCost = (
  cost: string,
  currentBalance: number,
  currencyDisplay: CurrencyDisplay,
) => {
  if (cost === "") {
    return "";
  } else if (currencyDisplay === "hours" && Number(cost) > 0) {
    return `${displayGpuHours(
      currentBalance / Number(cost),
    )} GPU hours remaining`;
  }
  return `$${cost}/hr`;
};

// Display GPU hours in thousands of hours
export const displayGpuKHours = (hours: number) =>
  `${(hours / 1000).toFixed(1)}k`;

// Display GPU hours in a human readable format
export const displayGpuHours = (hours: number) => {
  if (hours < 1 && hours > 0) {
    return "1"; // Round up to 1 whole hour
  }
  if (hours >= 1000) {
    return displayGpuKHours(hours);
  }
  return hours.toFixed(1).toString();
};

export type GrantOrg = {
  programId?: string | null;
  ProgramID?: string;
  labels?: Record<string, string> | null;
  Labels?: Record<string, string> | null;
};

const getGrantOrgLabels = (org?: GrantOrg | null) =>
  org?.labels ?? org?.Labels ?? null;

const isGrantProgram = (org: GrantOrg | null | undefined, program: string) => {
  const labels = getGrantOrgLabels(org);
  return (
    labels?.program === program ||
    org?.programId === program ||
    org?.ProgramID === program
  );
};

export const getCloudEndDateRaw = (org?: GrantOrg | null) => {
  const labels = getGrantOrgLabels(org);
  return (
    labels?.["Cloud End Date"]?.trim() ||
    labels?.["End Date"]?.trim() ||
    labels?.["Program End Date"]?.trim() ||
    null
  );
};

export const shouldShowAcademicGrantAccess = (
  org?: GrantOrg | null,
  currencyDisplay?: CurrencyDisplay | string | null,
) =>
  currencyDisplay === "hours" &&
  isGrantProgram(org, PROGRAMS.academicGrant) &&
  !isGrantProgram(org, PROGRAMS.innovationLab);

/** Date-only Cloud End Date labels are UTC midnight, matching Go time.Parse. */
export const parseCloudEndDate = (raw?: string | null): Date | null => {
  if (!raw?.trim()) return null;
  const value = raw.trim();

  const isoParts = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoParts) {
    const date = new Date(
      Date.UTC(
        Number(isoParts[1]),
        Number(isoParts[2]) - 1,
        Number(isoParts[3]),
      ),
    );
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const parts = value.split(/[/-]/);
  if (parts.length !== 3) return null;
  const month = Number(parts[0]);
  const day = Number(parts[1]);
  let year = Number(parts[2]);
  if (!month || !day || !year) return null;
  if (year < 100) year += 2000;
  const date = new Date(Date.UTC(year, month - 1, day));
  return Number.isNaN(date.getTime()) ? null : date;
};

const utcCalendarDayMs = (date: Date) =>
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());

export const formatProgramEndDate = (org?: GrantOrg | null) => {
  const parsed = parseCloudEndDate(getCloudEndDateRaw(org));
  if (!parsed) return null;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

export const getProgramDaysRemaining = (
  org?: GrantOrg | null,
  now: Date = new Date(),
) => {
  const parsed = parseCloudEndDate(getCloudEndDateRaw(org));
  if (!parsed) return null;
  return Math.round(
    (utcCalendarDayMs(parsed) - utcCalendarDayMs(now)) / 86_400_000,
  );
};

export const formatProgramDaysLeft = (
  org?: GrantOrg | null,
  now: Date = new Date(),
) => {
  const days = getProgramDaysRemaining(org, now);
  if (days == null) return null;
  if (days < 0) return "Ended";
  if (days === 0) return "Last day";
  if (days === 1) return "1 day left";
  return `${days} days left`;
};

export const formatGrantHeadline = (
  endDateLabel?: string | null,
  daysRemaining?: number | null,
) => {
  if (daysRemaining != null && daysRemaining < 0) {
    return endDateLabel
      ? `The Academic Grant program access ended on ${endDateLabel}.`
      : "The Academic Grant program access has ended.";
  }
  if (daysRemaining === 0) return "Your Academic Grant access ends today.";
  if (!endDateLabel || daysRemaining == null) {
    return "Days remaining are unavailable.";
  }
  const daysLeft =
    daysRemaining === 1 ? "1 day left" : `${daysRemaining} days left`;
  return `Your Academic Grant access ends on ${endDateLabel} (${daysLeft}).`;
};

export const grantAccessTone = (daysRemaining?: number | null) => {
  if (daysRemaining == null) return undefined;
  if (daysRemaining <= 0) return "critical" as const;
  if (daysRemaining <= 7) return "warning" as const;
  return "healthy" as const;
};

/** Date-only picker values are midnight; include that whole last day in usage queries. */
export const inclusiveEndOfDay = (date: Date) => {
  const end = new Date(date);
  if (
    end.getHours() === 0 &&
    end.getMinutes() === 0 &&
    end.getSeconds() === 0
  ) {
    end.setHours(23, 59, 59, 999);
  }
  return end;
};

/** Converts billing usage hours to days. The `/2` needs to change when duration only includes compute hours and storage duration is removed. */
export const formatRuntimeDays = (usageHours: number) => {
  const days = Math.round(((usageHours * 3_600) / 2 / 86_400) * 10) / 10;
  return `${days.toFixed(1)} ${days === 1 ? "day" : "days"}`;
};
